<template>
  <div class="order-detail" v-loading="detailLoading">
    <el-breadcrumb separator-class="el-icon-arrow-right">
      <el-breadcrumb-item :to="{ path: '/order-list' }">订单列表</el-breadcrumb-item>
      <el-breadcrumb-item>订单详情</el-breadcrumb-item>
    </el-breadcrumb>

    <div class="section order-info">
      <div class="section-title">订单信息</div>
      <div class="info-row">
        <span class="label">订单号：</span>
        <span class="value">{{ orderDetail.order_no }}</span>
      </div>
      <div class="info-row">
        <span class="label">买家：</span>
        <span class="value">{{ orderDetail.buyer_name }}</span>
      </div>
      <div class="info-row">
        <span class="label">买家手机号：</span>
        <span class="value">{{ orderDetail.buyer_mobile }}</span>
      </div>
      <div class="info-row" v-if="orderDetail.customer_source">
        <span class="label">客户来源：</span>
        <span class="value">{{ orderDetail.customer_source }}</span>
      </div>
      <div class="info-row">
        <span class="label">支付时间：</span>
        <span class="value">{{ orderDetail.payment_time }}</span>
      </div>
    </div>

    <div class="section order-status">
      <div class="section-title">订单状态</div>
      <div class="status-row">
        <span class="label">状态：</span>
        <el-tag :type="getOrderStatusType(orderDetail.order_status)" size="medium">
          {{ getOrderStatusText(orderDetail.order_status) }}
        </el-tag>
      </div>
      <div class="btn-row" v-if="canRefund">
        <el-button type="primary" @click="handleRefund" :loading="refundLoading">发起售后</el-button>
      </div>
    </div>

    <div class="section product-info">
      <div class="section-title">商品信息</div>
      <el-table :data="productList" border size="small" style="width: 100%">
        <el-table-column prop="product_name" label="商品名称" show-overflow-tooltip></el-table-column>
        <el-table-column prop="unit_price" label="单价(元)" show-overflow-tooltip>
          <template slot-scope="scope">
            ¥{{ formatPrice(scope.row.unit_price) }}
          </template>
        </el-table-column>
        <el-table-column prop="actual_amount" label="实付金额(元)" show-overflow-tooltip>
          <template slot-scope="scope">
            ¥{{ formatPrice(scope.row.actual_amount) }}
          </template>
        </el-table-column>
        <el-table-column prop="after_sales_status" label="售后状态" show-overflow-tooltip>
          <template slot-scope="scope">
            {{ getAfterSalesStatusText(scope.row.after_sales_status) }}
          </template>
        </el-table-column>
      </el-table>
    </div>

    <div class="section after-sales-info" v-if="hasAfterSales">
      <div class="section-title">售后信息</div>
      <el-table :data="afterSalesList" border size="small" style="width: 100%">
        <el-table-column prop="product_name" label="商品名称" show-overflow-tooltip></el-table-column>
        <el-table-column prop="unit_price" label="单价(元)" show-overflow-tooltip>
          <template slot-scope="scope">
            ¥{{ formatPrice(scope.row.unit_price) }}
          </template>
        </el-table-column>
        <el-table-column prop="actual_amount" label="实付金额(元)" show-overflow-tooltip>
          <template slot-scope="scope">
            ¥{{ formatPrice(scope.row.actual_amount) }}
          </template>
        </el-table-column>
        <el-table-column prop="after_sales_status" label="售后状态" show-overflow-tooltip>
          <template slot-scope="scope">
            {{ getAfterSalesStatusText(scope.row.after_sales_status) }}
          </template>
        </el-table-column>
        <el-table-column prop="refund_amount" label="退款金额(元)" show-overflow-tooltip>
          <template slot-scope="scope">
            ¥{{ formatPrice(scope.row.refund_amount) }}
          </template>
        </el-table-column>
        <el-table-column prop="refund_time" label="退款时间" show-overflow-tooltip></el-table-column>
      </el-table>
    </div>

    <div class="section after-sales-info" v-else>
      <div class="section-title">售后信息</div>
      <div class="empty-tip"></div>
    </div>

    <el-dialog title="审核售后" :visible.sync="refundDialogVisible" width="560px" center class="refund-dialog">
      <div class="refund-dialog-content">
        <div class="refund-row">
          <span class="label">订单号：</span>
          <span class="value">{{ orderDetail.order_no }}</span>
        </div>
        <div class="refund-section">
          <div class="refund-section-title">商品信息</div>
          <el-table :data="productList" border size="small" style="width: 100%">
            <el-table-column prop="product_name" label="商品名称" align="center" min-width="100"></el-table-column>
            <el-table-column prop="unit_price" label="单价(元)" width="100" align="center">
              <template slot-scope="scope">¥{{ formatPrice(scope.row.unit_price) }}</template>
            </el-table-column>
            <el-table-column prop="actual_amount" label="实付金额" width="100" align="center">
              <template slot-scope="scope">¥{{ formatPrice(scope.row.actual_amount) }}</template>
            </el-table-column>
            <el-table-column prop="after_sales_status" label="售后状态" width="100" align="center">
              <template slot-scope="scope">{{ getAfterSalesStatusInDialog(scope.row.after_sales_status) }}</template>
            </el-table-column>
          </el-table>
        </div>
        <div class="refund-row">
          <span class="label">订单状态：</span>
          <el-tag :type="getOrderStatusType(orderDetail.order_status)" size="small">{{ getOrderStatusText(orderDetail.order_status) }}</el-tag>
        </div>
        <div class="refund-row">
          <span class="label"><span class="required-star">*</span>退款方式：</span>
          <el-radio-group v-model="refundForm.refundType">
            <el-radio label="full">全额退款</el-radio>
          </el-radio-group>
        </div>
        <div class="refund-row">
          <span class="label">退款金额：</span>
          <span class="value">{{ formatPrice(orderDetail.actual_payment) }}元</span>
        </div>
        <div class="refund-row refund-reason">
          <span class="label"><span class="required-star">*</span>售后原因：</span>
          <div class="reason-input-wrap">
            <el-input type="textarea" v-model="refundForm.reason" placeholder="请输入售后备注说明" maxlength="100" show-word-limit :rows="4"></el-input>
          </div>
        </div>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button @click="closeRefundDialog">取 消</el-button>
        <el-button type="primary" @click="submitRefund">确 认</el-button>
      </span>
    </el-dialog>

    <el-dialog title="提示" :visible.sync="confirmDialogVisible" width="420px" center class="confirm-dialog" :close-on-click-modal="true">
      <div class="confirm-dialog-content">确认发起售后退款？</div>
      <span slot="footer" class="dialog-footer">
        <el-button @click="confirmDialogVisible = false">取 消</el-button>
        <el-button type="primary" @click="confirmRefundSubmit">确 定</el-button>
      </span>
    </el-dialog>

    <el-dialog title="提示" :visible.sync="tipDialogVisible" width="420px" center class="tip-dialog" :close-on-click-modal="true">
      <div class="tip-dialog-content">订单已处于退款中或已退款无需重复申请</div>
      <span slot="footer" class="dialog-footer">
        <el-button type="primary" @click="tipDialogVisible = false">确 定</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
export default {
  name: 'OrderDetail',
  data() {
    return {
      detailLoading: false,
      orderDetail: {
        order_no: '',
        buyer_name: '',
        buyer_mobile: '',
        customer_source: '',
        order_status: '',
        after_sales_status: '',
        product_name: '',
        unit_price: 0,
        actual_payment: 0,
        payment_time: '',
        payment_method: '',
        transaction_id: '',
        refund_info: null,
        refund_time: null,
        refund_amount: null,
        refund_reason: null
      },
      refundDialogVisible: false,
      refundLoading: false,
      confirmDialogVisible: false,
      tipDialogVisible: false,
      refundForm: {
        refundType: 'full',
        reason: ''
      },
      orderStatusOptions: [],
      refundStatusOptions: []
    }
  },
  computed: {
    productList() {
      return [{
        product_name: this.orderDetail.product_name,
        unit_price: this.orderDetail.unit_price,
        actual_amount: this.orderDetail.actual_payment,
        actual_payment: this.orderDetail.actual_payment,
        after_sales_status: this.orderDetail.after_sales_status
      }]
    },
    hasAfterSales() {
      const a = this.orderDetail.after_sales_status
      return ['PROCESSING', 'SUCCESS', 'ABNORMAL', 'refunded', 'applied'].includes(a)
    },
    afterSalesList() {
      if (!this.hasAfterSales) return []
      return [{
        product_name: this.orderDetail.product_name,
        unit_price: this.orderDetail.unit_price,
        actual_amount: this.orderDetail.actual_payment,
        actual_payment: this.orderDetail.actual_payment,
        after_sales_status: this.orderDetail.after_sales_status,
        refund_amount: this.orderDetail.refund_amount != null ? this.orderDetail.refund_amount : this.orderDetail.actual_payment,
        refund_time: this.orderDetail.refund_time || '-'
      }]
    },
    canRefund() {
      // 仅【已支付】且未处于退款中或已退款时显示按钮
      const s = this.orderDetail.order_status
      const a = this.orderDetail.after_sales_status
      const paidStatuses = ['SUCCESS', 'paid']
      const refundingOrRefunded = ['PROCESSING', 'SUCCESS', 'applied', 'refunded']
      return paidStatuses.includes(s) && !refundingOrRefunded.includes(a)
    }
  },
  created() {
    this.fetchOrderEnums()
    this.loadOrderDetail()
  },
  methods: {
    fetchOrderEnums() {
      this.$getOrderEnums().then((enums) => {
        this.orderStatusOptions = enums.order_status || []
        this.refundStatusOptions = enums.refund_status || []
      })
    },
    loadOrderDetail() {
      const orderNo = this.$route.query.order_no
      if (!orderNo) {
        this.$message.warning('缺少订单号')
        return
      }
      this.detailLoading = true
      this.$get(`/v1/admin/order/detail?order_no=${encodeURIComponent(orderNo)}`, {})
        .then((res) => {
          this.detailLoading = false
          if (res.data && res.data.status === 'success' && res.data.data) {
            const d = res.data.data
            const refund = d.refund_info || null
            this.orderDetail = {
              order_no: d.order_no || orderNo,
              buyer_name: d.buyer_name || '-',
              buyer_mobile: d.buyer_mobile || '-',
              customer_source: d.customer_source || '-',
              order_status: d.order_status || 'SUCCESS',
              after_sales_status: refund ? refund.refund_status : 'NONE',
              product_name: d.product_name || '-',
              unit_price: d.unit_price != null ? d.unit_price : 0,
              actual_payment: d.actual_payment != null ? d.actual_payment : 0,
              payment_time: d.payment_time || '-',
              payment_method: d.payment_method || '-',
              transaction_id: d.transaction_id || '-',
              refund_info: refund,
              refund_time: refund ? refund.create_time : null,
              refund_amount: refund ? refund.refund_amount : null,
              refund_reason: refund ? refund.reason : null
            }
          } else {
            this.$message.error((res.data && res.data.msg) || '获取订单详情失败')
          }
        })
        .catch(() => {
          this.detailLoading = false
          this.$message.error('获取订单详情失败')
        })
    },
    formatPrice(val) {
      if (val === undefined || val === null) return '0.00'
      const num = typeof val === 'number' ? val : parseFloat(val)
      if (isNaN(num)) return '0.00'
      return (num >= 100 ? num / 100 : num).toFixed(2)
    },
    getOrderStatusType(status) {
      const map = {
        SUCCESS: 'success',
        REFUND: 'danger',
        NOTPAY: 'warning',
        CLOSED: 'info',
        REVOKED: 'info',
        USERPAYING: 'warning',
        PAYERROR: 'danger',
        pending: 'warning',
        paid: 'success',
        completed: 'info',
        cancelled: 'info',
        refunded: 'danger'
      }
      return map[status] || 'info'
    },
    getOrderStatusText(status) {
      const opt = (this.orderStatusOptions || []).find((o) => o.value === status)
      if (opt) return opt.label
      const fallback = {
        pending: '待支付',
        paid: '已支付',
        completed: '已完成',
        cancelled: '已取消',
        refunded: '已退款',
        SUCCESS: '支付成功',
        REFUND: '转入退款',
        NOTPAY: '未支付',
        CLOSED: '已关闭',
        REVOKED: '已撤销',
        USERPAYING: '用户支付中',
        PAYERROR: '支付失败'
      }
      return fallback[status] || (status ? String(status) : '-')
    },
    getAfterSalesStatusText(status) {
      if (status === undefined || status === null || status === '') return '-'
      const opt = (this.refundStatusOptions || []).find((o) => o.value === status)
      if (opt) return opt.label
      const fallback = {
        PROCESSING: '退款处理中',
        SUCCESS: '退款成功',
        ABNORMAL: '退款异常',
        CLOSED: '退款关闭',
        NONE: '无售后',
        none: '无售后',
        applied: '申请中',
        refunded: '已退款',
        rejected: '已拒绝',
        refund_failed: '退款失败'
      }
      return fallback[status] || String(status)
    },
    getAfterSalesStatusInDialog(status) {
      if (status === undefined || status === null || status === '') return '未售后'
      const opt = (this.refundStatusOptions || []).find((o) => o.value === status)
      if (opt) return opt.label
      const fallback = {
        NONE: '未售后',
        PROCESSING: '退款中',
        SUCCESS: '已退款',
        ABNORMAL: '退款异常',
        CLOSED: '退款关闭',
        none: '未售后',
        applied: '退款中',
        refunded: '已退款',
        rejected: '已拒绝',
        refund_failed: '退款失败'
      }
      return fallback[status] || String(status)
    },
    handleRefund() {
      // 校验：订单状态须为【已支付】，且未处于退款中或已退款
      const s = this.orderDetail.order_status
      const a = this.orderDetail.after_sales_status
      const paidStatuses = ['SUCCESS', 'paid']
      const refundingOrRefunded = ['PROCESSING', 'SUCCESS', 'applied', 'refunded']
      const isPaid = paidStatuses.includes(s)
      const isRefundingOrRefunded = refundingOrRefunded.includes(a)
      if (!isPaid) {
        this.$message.warning('订单未支付，无法发起售后')
        return
      }
      if (isRefundingOrRefunded) {
        this.tipDialogVisible = true
        return
      }
      this.refundForm = { refundType: 'full', reason: '' }
      this.refundDialogVisible = true
    },
    closeRefundDialog() {
      this.refundDialogVisible = false
    },
    submitRefund() {
      if (!this.refundForm.reason || !this.refundForm.reason.trim()) {
        this.$message.warning('请输入售后原因')
        return
      }
      this.confirmDialogVisible = true
    },
    confirmRefundSubmit() {
      this.confirmDialogVisible = false
      this.doRefund()
    },
    doRefund() {
      this.refundDialogVisible = false
      this.confirmDialogVisible = false
      this.refundLoading = true
      this.$post('/v1/admin/order/refund', {
        order_no: this.orderDetail.order_no,
        refund_reason: this.refundForm.reason ? this.refundForm.reason.trim() : '',
        operator: localStorage.getItem('username') || ''
      })
        .then((res) => {
          // http 拦截器将错误 resolve，4xx/5xx 时 res 为 err 对象
          if (res && res.response) {
            const data = (res.response || {}).data
            this.$message.error(this.getRefundErrorMessage(data))
            return
          }
          const data = res && res.data
          if (data && data.status === 'success') {
            const refundStatus = (data.data && data.data.status) || ''
            if (refundStatus === 'SUCCESS') {
              this.$message.success('退款成功')
            } else {
              this.$message.success('退款已提交，正在处理中')
            }
            this.loadOrderDetail()
          } else {
            this.$message.error(this.getRefundErrorMessage(data))
          }
        })
        .catch(() => {
          this.$message.error('退款失败')
        })
        .finally(() => {
          this.refundLoading = false
        })
    },
    getRefundErrorMessage(data) {
      if (!data) return '退款失败'
      const resid = data.resid
      const msg = data.msg || ''
      if (resid === 404) return msg || '订单不存在'
      if (resid === 400) return msg || '已退款'
      return msg || '退款失败'
    }
  }
}
</script>

<style lang="stylus" scoped>
.order-detail
  .section
    margin-top 24px
    padding 20px
    background #fff
    border 1px solid #EBEEF5
    border-radius 4px
  .section-title
    font-size 16px
    font-weight 600
    color #000
    margin-bottom 16px
  .order-info
    .info-row
      display inline-block
      margin-right 32px
      margin-bottom 8px
      .label
        color #606266
      .value
        color #303133
  .order-status
    .status-row
      margin-bottom 12px
      .label
        margin-right 8px
        color #606266
    .btn-row
      margin-top 12px
  .empty-tip
    min-height 60px

</style>

<style lang="stylus">
.refund-dialog
  .refund-dialog-content
    padding 0 10px
    .refund-row
      display flex
      align-items flex-start
      margin-bottom 18px
      .label
        color #606266
        min-width 90px
        line-height 32px
        flex-shrink 0
        text-align left
      .required-star
        color #f56c6c
        margin-right 2px
      .value
        color #303133
        line-height 32px
    .refund-section
      margin 18px 0
    .refund-section-title
      font-size 14px
      margin-bottom 10px
      color #303133
    .refund-reason
      .label
        line-height 1
        padding-top 6px
      .reason-input-wrap
        flex 1
        .el-textarea
          width 100%

.confirm-dialog
  .confirm-dialog-content
    text-align center
    font-size 15px
    color #303133
    line-height 1.6
    padding 20px 0

.tip-dialog
  .tip-dialog-content
    text-align center
    font-size 15px
    color #303133
    line-height 1.6
    padding 20px 0
</style>



// WEBPACK FOOTER //
// src/pages/OrderCenter/OrderDetail.vue